import React, { useState, useEffect, useCallback } from "react";

import { SKILL_ICONS } from "../agentWorkspace/SkillCard.tsx";
import { customAgentAPI, onboardingAPI, userSkillsAPI, linkedinAPI, gmailAPI, calendarAPI } from "../../services/api.js";
import { useSkillCatalogue } from "../../hooks/useSkillCatalogue.ts";
import { Search, RefreshCw, Plus, Minus, ChevronDown, Mail, CalendarDays, Handshake, Link, Linkedin } from "lucide-react";
import AddSkillModal from "../modals/AddSkillModal.tsx";
import toast from "react-hot-toast";
import { alpha } from '../../utils/alpha';

const T = {
  page: "var(--lyra-color-surface-2)",
  surface: "var(--lyra-color-surface)",
  border: "var(--lyra-color-border)",
  ink: "var(--lyra-color-text)",
  ink3: "var(--app-text-2)",
  ink4: "var(--app-text-3)",
  accent: "var(--app-primary-mid)",
  accentLight: "var(--app-primary-soft)",
};

const CATEGORY_COLORS: Record<string, string> = {
  research:   "#5B7FD4",
  coding:     "var(--app-primary-mid)",
  sales:      "#C0533A",
  marketing:  "#C08A3A",
  data:       "#8B5CF6",
  writing:    "#D45B8A",
  utilities:  "#6A9A8A",
};

type SortOption = "Popular" | "Name A–Z" | "Name Z–A";

export default function SkillsPage() {
  
  const [skills, setSkills] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [enabledSkillIds, setEnabledSkillIds] = useState<string[]>([]);
  const [linkedinConnected, setLinkedinConnected] = useState<boolean>(false);
  const [gmailConnected, setGmailConnected] = useState<boolean>(false);
  const [calendarConnected, setCalendarConnected] = useState<boolean>(false);
  const [selectedSkills, setSelectedSkills] = useState<Record<string, string[]>>({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "installed" | "not_installed">("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<SortOption>("Popular");
  const [sortOpen, setSortOpen] = useState(false);
  const [showAddSkill, setShowAddSkill] = useState(false);

  // Shared catalogue from server (cached via useSkillCatalogue hook)
  const { skills: catalogueSkills, loading: catalogueLoading } = useSkillCatalogue();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const templatesRes = await customAgentAPI.getTemplates().catch(() => ([]));
      // Home's +Tools picker is backed by this registry. Merge it here too so
      // the library cannot silently advertise a different set of capabilities.
      const token = localStorage.getItem('authToken');
      const registryRes = await fetch(`${(process.env.REACT_APP_API_URL as string) || ''}/api/skills-registry`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).then((r) => r.ok ? r.json() : { skills: [] }).catch(() => ({ skills: [] }));
      const registryItems = (registryRes.skills || []).map((s: any) => ({
        id: s.id, name: s.name, description: s.description, icon: s.icon || 'Wrench',
        domain: s.domain || 'utilities', category: s.domain || 'utilities', tags: [], uses: '0',
        official: true, source: 'lyraskills', lyraSkill: true, installed: s.status === 'available',
      }));
      const known = new Set(catalogueSkills.map((s: any) => s.id));
      const catalogueItems: any[] = [...catalogueSkills, ...registryItems.filter((s: any) => !known.has(s.id))];

      // load user-created skills and merge them into the listing
      let userSkills: any[] = [];
      try {
        userSkills = await userSkillsAPI.list();
      } catch (e) {
        userSkills = [];
      }

      const merged = catalogueItems.map((ws: any) => ({
        id: ws.id,
        name: ws.name,
        description: ws.description,
        icon: ws.icon,
        category: (ws.domain || ws.category || "").toLowerCase(),
        installed: !!ws.installed,
        prompt: ws.prompt || ws.systemPrompt || ws.promptText || undefined,
        source: ws.source || "catalogue",
        lyraSkill: ws.lyraSkill || false,
        connectRoute: ws.connectRoute || undefined,
      }));

      const templates = Array.isArray(templatesRes) ? templatesRes : templatesRes?.templates || [];
      const mappedTemplates = templates.map((t: any) => ({
        id: `mktg-${t._id}`,
        name: t.name,
        description: t.description || t.summary || "",
        icon: t.icon || "Megaphone",
        category: "marketing",
        installed: false,
        prompt: t.systemPrompt || t.system || t.prompt || "",
        _template: t,
        source: "marketing",
      }));

      // fetch user onboarding selections
      let onboarding = null;
      try {
        const ob = await onboardingAPI.get();
        onboarding = ob?.onboarding || null;
      } catch (e) {
        console.warn('Failed to load onboarding', e);
      }

      const sel: Record<string, string[]> = {};
      if (onboarding && onboarding.selectedSkills) {
        // onboarding.selectedSkills may be a map/object from server
        const src = onboarding.selectedSkills;
        if (typeof src === 'object') {
          Object.keys(src).forEach(k => { sel[k] = Array.isArray(src[k]) ? src[k] : []; });
        }
      }

      const mappedUserSkills = (Array.isArray(userSkills) ? userSkills : []).map((s: any) => ({
        id: s._id || s.id,
        name: s.name,
        description: s.description || '',
        icon: s.icon || '🔧',
        category: (s.domain || 'user').toLowerCase(),
        installed: true,
        prompt: undefined,
        source: 'user',
        _userSkill: s,
      }));

      setSkills([...mappedUserSkills, ...merged, ...mappedTemplates]);
      setSelectedSkills(sel);

      // fetch enabled skill IDs from onboarding catalogue
      try {
        const onb = await onboardingAPI.get().catch(() => null);
        setEnabledSkillIds(onb?.onboarding?.selectedSkillIds || []);
      } catch (e) {
        // non-fatal
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load skills");
    } finally {
      setLoading(false);
    }
  }, [catalogueSkills]);

  useEffect(() => { load(); }, [load]);

  // Check integrations status so built-in skills reflect actual connections
  const refreshIntegrations = async () => {
    try {
      const [ln, gm, cal] = await Promise.all([
        linkedinAPI.status().catch(() => ({ connected: false })),
        gmailAPI.status().catch(() => ({ connected: false })),
        calendarAPI.status().catch(() => ({ connected: false })),
      ]);
      setLinkedinConnected(!!ln?.connected);
      setGmailConnected(!!gm?.connected);
      setCalendarConnected(!!cal?.connected);
    } catch (e) {
      setLinkedinConnected(false);
      setGmailConnected(false);
      setCalendarConnected(false);
    }
  };

  useEffect(() => { refreshIntegrations(); }, []);

  // Built-in Lyra skills (always shown) with Lucide icons + connect actions
  const LYRA_ICON_MAP: Record<string, any> = {
    'lyra:gmail-reader':      Mail,
    'lyra:calendar-assistant': CalendarDays,
    'lyra:teams-connector':   Handshake,
    'linkedin-post':          Linkedin,
  };

  const LYRA_SKILLS = [
    {
      id: 'lyra:gmail-reader',
      name: 'Gmail Reader',
      description: 'List and summarise unread Gmail messages from your connected account. Read-only — no sending.',
      category: 'communication',
      color: '#EA4335',
      colorBg: 'var(--lyra-color-danger-bg)',
      connectRoute: '/settings?tab=integrations',
      endpoints: [
        { label: 'List messages', example: 'GET /api/gmail/messages?q=is:unread' },
        { label: 'Get message',  example: 'GET /api/gmail/messages/:id' },
      ],
    },
    {
      id: 'lyra:calendar-assistant',
      name: 'Calendar Assistant',
      description: 'Read upcoming events and create bookings in your connected Google Calendar.',
      category: 'productivity',
      color: '#1A73E8',
      colorBg: 'var(--app-primary-soft)',
      connectRoute: '/settings?tab=integrations',
      endpoints: [
        { label: 'List events',  example: 'GET /api/calendar/events?timeMin=…' },
        { label: 'Create event', example: 'POST /api/calendar/events { summary, start, end }' },
      ],
    },
    {
      id: 'lyra:teams-connector',
      name: 'Teams Connector',
      description: 'Read channel messages and post notifications to Microsoft Teams.',
      category: 'communication',
      color: '#6264A7',
      colorBg: 'var(--app-primary-soft)',
      connectRoute: '/settings?tab=integrations',
      endpoints: [
        { label: 'List messages',    example: 'GET /api/teams/messages?channelId=…' },
        { label: 'Post to channel',  example: 'POST /api/teams/post { channelId, body }' },
      ],
    },
    {
      id: 'linkedin-post',
      name: 'LinkedIn Post',
      description: 'Craft and publish professional posts directly to your connected LinkedIn profile.',
      category: 'social',
      color: '#0A66C2',
      colorBg: '#0A66C215',
      connectRoute: '/integrations',
      endpoints: [
        { label: 'Post text',  example: 'POST /auth/linkedin/post { text }' },
        { label: 'Status',     example: 'GET /auth/linkedin/status' },
      ],
    },
  ];



  // Derive category counts
  const allCategories = Array.from(new Set(skills.map(s => s.category).filter(Boolean)));
  const categoryCounts: Record<string, number> = {};
  allCategories.forEach(c => {
    categoryCounts[c] = skills.filter(s => s.category === c).length;
  });

  // Determine installed/added status using server-installed flag OR user's selectedSkills
  const isSkillSelected = (skill: any) => {
    const domain = (skill.category || skill.domain || 'general').toLowerCase();
    return Array.isArray(selectedSkills[domain]) && selectedSkills[domain].includes(skill.id);
  };

  const installedCount = skills.filter(s => s.installed || isSkillSelected(s)).length;
  const notInstalledCount = skills.length - installedCount;

  const filtered = skills
    .filter(s => {
      const installed = s.installed || isSkillSelected(s);
      if (statusFilter === "installed" && !installed) return false;
      if (statusFilter === "not_installed" && installed) return false;
      if (categoryFilter !== "all" && s.category !== categoryFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (s.name || "").toLowerCase().includes(q) ||
          (s.description || "").toLowerCase().includes(q) ||
          (s.category || "").toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "Name A–Z") return a.name.localeCompare(b.name);
      if (sortBy === "Name Z–A") return b.name.localeCompare(a.name);
      return 0; // Popular = original order
    });

  const handleInstall = (skill: any) => {
    // Toggle user selection (add/remove preference)
    const domain = (skill.category || skill.domain || 'general').toLowerCase();
    const currently = selectedSkills[domain] || [];
    const isAdded = currently.includes(skill.id);
    const next = isAdded ? currently.filter(s => s !== skill.id) : Array.from(new Set([...currently, skill.id]));

    // optimistic update
    setSelectedSkills(prev => ({ ...prev, [domain]: next }));
    onboardingAPI.toggleSkill(domain, skill.id, isAdded ? 'remove' : 'add')
      .then(res => {
        toast.success(isAdded ? `${skill.name} removed` : `${skill.name} added`);
      })
      .catch(err => {
        console.error('Failed saving skill selection', err);
        // rollback
        setSelectedSkills(prev => ({ ...prev, [domain]: currently }));
        toast.error('Failed to save selection');
      });
  };

  return (
    <div style={{ minHeight: "100vh", background: T.page, fontFamily: "Inter, system-ui, sans-serif", display: "flex" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .skill-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.08); transform: translateY(-1px); transition: all 0.15s ease; }
        .skill-card { transition: all 0.15s ease; }
        .sidebar-item:hover { background: ${T.accentLight}; cursor: pointer; }
        .sort-item:hover { background: ${T.page}; cursor: pointer; }
        .install-btn:hover { opacity: 0.85; }
      `}</style>

      {/* ── LEFT SIDEBAR ── */}
      <div style={{
        width: 180,
        minWidth: 180,
        background: T.surface,
        borderRight: `1px solid ${T.border}`,
        padding: "24px 0",
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}>
        {/* Status section */}
        <div style={{ padding: "0 16px" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
            Status
          </div>
          {[
            { label: "All skills", value: "all", count: skills.length },
            { label: "Installed", value: "installed", count: installedCount },
            { label: "Not installed", value: "not_installed", count: notInstalledCount },
          ].map(item => (
            <div
              key={item.value}
              className="sidebar-item"
              onClick={() => setStatusFilter(item.value as any)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 8px",
                borderRadius: 6,
                marginBottom: 2,
                background: statusFilter === item.value ? T.accentLight : "transparent",
              }}
            >
              <span style={{ fontSize: 13, color: statusFilter === item.value ? T.accent : T.ink, fontWeight: statusFilter === item.value ? 600 : 400 }}>
                {item.label}
              </span>
              <span style={{ fontSize: 12, color: T.ink4, fontWeight: 500 }}>{item.count}</span>
            </div>
          ))}
        </div>

        {/* Category section */}
        <div style={{ padding: "0 16px" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.ink4, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
            Category
          </div>
          {allCategories.map(cat => {
            const color = CATEGORY_COLORS[cat] || T.accent;
            return (
              <div
                key={cat}
                className="sidebar-item"
                onClick={() => setCategoryFilter(categoryFilter === cat ? "all" : cat)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 8px",
                  borderRadius: 6,
                  marginBottom: 2,
                  background: categoryFilter === cat ? T.accentLight : "transparent",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: categoryFilter === cat ? T.accent : T.ink, fontWeight: categoryFilter === cat ? 600 : 400, textTransform: "capitalize" }}>
                    {cat}
                  </span>
                </div>
                <span style={{ fontSize: 12, color: T.ink4, fontWeight: 500 }}>{categoryCounts[cat]}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ flex: 1, padding: "24px 24px 28px", minWidth: 0 }}>
        {/* Top bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, gap: 12 }}>
          {/* Search */}
          <div style={{ position: "relative", flex: 1, maxWidth: 360 }}>
            <Search size={14} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: T.ink4 }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search skills..."
              style={{
                width: "100%",
                padding: "8px 12px 8px 34px",
                borderRadius: 8,
                border: `1px solid ${T.border}`,
                background: T.surface,
                fontSize: 13,
                color: T.ink,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* Add Skill button */}
            <button
              onClick={() => setShowAddSkill(true)}
              title="Add Skill"
              style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, cursor: "pointer", display: "flex", alignItems: "center" }}
            >
              <Plus size={14} color={T.ink3} />
            </button>
            {/* Sort dropdown */}
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setSortOpen(o => !o)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  background: T.surface,
                  cursor: "pointer",
                  fontSize: 13,
                  color: T.ink,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontWeight: 500,
                }}
              >
                Sort: {sortBy} <ChevronDown size={13} />
              </button>
              {sortOpen && (
                <div style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 4px)",
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  overflow: "hidden",
                  zIndex: 50,
                  minWidth: 150,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                }}>
                  {(["Popular", "Name A–Z", "Name Z–A"] as SortOption[]).map(opt => (
                    <div
                      key={opt}
                      className="sort-item"
                      onClick={() => { setSortBy(opt); setSortOpen(false); }}
                      style={{
                        padding: "8px 14px",
                        fontSize: 13,
                        color: sortBy === opt ? T.accent : T.ink,
                        fontWeight: sortBy === opt ? 600 : 400,
                      }}
                    >
                      {opt}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Refresh */}
            <button
              onClick={load}
              style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, cursor: "pointer", display: "flex", alignItems: "center" }}
            >
              <RefreshCw size={14} color={T.ink3} />
            </button>
          </div>
        </div>


        {/* ── Lyra Built-in Skills ── */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: T.ink }}>Lyra Skills</h3>
            <span style={{ fontSize: 11, color: '#6b21a8', background: 'var(--app-primary-soft)', borderRadius: 99, padding: '2px 10px', fontWeight: 600, border: '1px solid var(--app-primary-soft)' }}>Built-in</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px,1fr))', gap: 10 }}>
            {LYRA_SKILLS.map((s: any) => {
              const LyraIcon = LYRA_ICON_MAP[s.id] || Mail;
              // Consider integration state for certain built-in skills (e.g. LinkedIn, Gmail, Calendar)
              const isEnabled = enabledSkillIds.includes(s.id)
                || (s.id === 'linkedin-post' && linkedinConnected)
                || (s.id === 'lyra:gmail-reader' && gmailConnected)
                || (s.id === 'lyra:calendar-assistant' && calendarConnected);
              return (
                <div key={s.id} className="skill-card" style={{
                  background: T.surface, border: `1px solid ${isEnabled ? alpha(s.color, '44') : T.border}`,
                  borderRadius: 10, padding: '14px 14px 12px', display: 'flex', flexDirection: 'column', gap: 10,
                  boxShadow: isEnabled ? `0 0 0 1px ${alpha(s.color, '22')}` : 'none',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: 8, background: s.colorBg,
                        border: `1px solid ${alpha(s.color, '33')}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <LyraIcon size={18} color={s.color} />
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{s.name}</div>
                        <span style={{ fontSize: 10, color: T.ink4, textTransform: 'capitalize' }}>{s.category}</span>
                      </div>
                    </div>
                    {isEnabled
                      ? <span style={{ fontSize: 10, fontWeight: 600, color: T.green, background: T.greenBg, borderRadius: 99, padding: '2px 8px', border: `1px solid ${T.greenBd}`, flexShrink: 0 }}>Active</span>
                      : <a href={s.connectRoute} style={{ fontSize: 11, fontWeight: 600, color: s.color, background: s.colorBg, borderRadius: 6, padding: '4px 10px', textDecoration: 'none', border: `1px solid ${alpha(s.color, '33')}`, flexShrink: 0 }}>Connect</a>
                    }
                  </div>
                  <div style={{ fontSize: 12, color: T.ink3, lineHeight: 1.45 }}>{s.description}</div>
                  <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {s.endpoints.map((ep: any) => (
                      <div key={ep.label} style={{ fontSize: 11 }}>
                        <span style={{ color: T.ink4, fontWeight: 500 }}>{ep.label}: </span>
                        <code style={{ background: T.surface2, border: `1px solid ${T.border}`, padding: '1px 5px', borderRadius: 4, fontSize: 10, color: T.ink3 }}>{ep.example}</code>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
          {(loading || catalogueLoading) ? (
            <div style={{ gridColumn: "1/-1", padding: 48, textAlign: "center", color: T.ink4 }}>
              <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                <div style={{ width: 18, height: 18, borderRadius: 9, border: `2px solid ${T.accent}`, animation: "spin 1s linear infinite" }} />
                Loading...
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ gridColumn: "1/-1", padding: 48, textAlign: "center", color: T.ink4 }}>No skills found.</div>
          ) : (
            filtered.map((s: any) => {
              const Icon = SKILL_ICONS[s.icon] || (() => <span style={{ fontSize: 16 }}>{s.icon || "🔧"}</span>);
              const catColor = CATEGORY_COLORS[s.category] || T.accent;
              const catBg = alpha(catColor, '18');

              return (
                <div
                  key={s.id}
                  className="skill-card"
                  style={{
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderRadius: 10,
                    padding: "14px 14px 12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    position: "relative",
                  }}
                >
                  {/* Top row: icon + action button */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      background: T.accentLight,
                      border: `1px solid ${T.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <Icon size={18} />
                    </div>

                    {(() => {
                      const domain = (s.category || s.domain || 'general').toLowerCase();
                      const isAdded = Array.isArray(selectedSkills[domain]) && selectedSkills[domain].includes(s.id);
                      // Styles: Add = green variation; Remove = red/removal variation
                      const ADD_BG = T.accent; // green
                      const ADD_COLOR = 'var(--lyra-color-surface)';
                      const REMOVE_COLOR = '#C0533A'; // reddish

                      return (
                        <button
                          className="install-btn"
                          onClick={() => handleInstall(s)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 12px",
                            borderRadius: 7,
                            border: isAdded ? `1px solid ${REMOVE_COLOR}` : `1px solid ${ADD_BG}`,
                            background: isAdded ? T.surface : ADD_BG,
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 600,
                            color: isAdded ? REMOVE_COLOR : ADD_COLOR,
                          }}
                        >
                          {isAdded ? <Minus size={12} /> : <Plus size={12} color={ADD_COLOR} />}
                          {isAdded ? 'Remove' : 'Add'}
                        </button>
                      );
                    })()}
                  </div>

                  {/* Name & description */}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 3 }}>{s.name}</div>
                    <div style={{ fontSize: 12, color: T.ink3, lineHeight: 1.45 }}>{s.description}</div>
                  </div>

                  {/* Category badge */}
                  {s.category && (
                    <div style={{ marginTop: "auto" }}>
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "3px 8px",
                        borderRadius: 20,
                        background: catBg,
                        fontSize: 11,
                        fontWeight: 600,
                        color: catColor,
                        textTransform: "capitalize",
                      }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: catColor }} />
                        {s.category}
                      </span>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <AddSkillModal
        isOpen={showAddSkill}
        onClose={() => setShowAddSkill(false)}
        onCreated={(created: any) => {
          // Invalidate shared catalogue cache so SkillLibrarySidebar also picks up new skill
          invalidateSkillCatalogue();
          // prepend created skill to list for immediate feedback
          setSkills(prev => [
            {
              id: created._id || created.id || `user-${Date.now()}`,
              name: created.name,
              description: created.description || '',
              icon: created.icon || '🔧',
              category: (created.domain || 'user').toLowerCase(),
              installed: true,
              prompt: undefined,
              source: 'user',
              _userSkill: created,
            },
            ...prev,
          ]);
          setShowAddSkill(false);
          toast.success('Skill saved');
        }}
      />

      {/* Close sort dropdown on outside click */}
      {sortOpen && (
        <div
          onClick={() => setSortOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 40 }}
        />
      )}
    </div>
  );
}
